import { Injectable, inject } from '@angular/core';
import { HTTP_AP_DeleteAdvisorUserAccessToken_Request } from '@api-new/authservice/HTTP_AP_DeleteAdvisorUserAccessToken_Request';
import { HTTP_AP_ImpersonateClientUser_Request } from '@api-new/authservice/HTTP_AP_ImpersonateClientUser_Request';
import { HTTP_AP_ImpersonateClientUser_Response } from '@api-new/authservice/HTTP_AP_ImpersonateClientUser_Response';
import { HTTP_AP_ListAdvisorUserAccessTokens_Response } from '@api-new/authservice/HTTP_AP_ListAdvisorUserAccessTokens_Response';
import { HTTP_AP_ProvideAdvisorUserResetPasswordCode_Request } from '@api-new/authservice/HTTP_AP_ProvideAdvisorUserResetPasswordCode_Request';
import { HTTP_AP_ResetPassword_WithCode_Request } from '@api-new/authservice/HTTP_AP_ResetPassword_WithCode_Request';
import { HTTP_AP_SignIn_WithPassword_Request } from '@api-new/authservice/HTTP_AP_SignIn_WithPassword_Request';
import { HTTP_AP_SignIn_WithPassword_Response } from '@api-new/authservice/HTTP_AP_SignIn_WithPassword_Response';
import { HTTP_AP_SignIn_WithProvider_Authorize_Request } from '@api-new/authservice/HTTP_AP_SignIn_WithProvider_Authorize_Request';
import { HTTP_AP_SignIn_WithProvider_Authorize_Response } from '@api-new/authservice/HTTP_AP_SignIn_WithProvider_Authorize_Response';
import { HTTP_AP_SignIn_WithProvider_Initialize_Request } from '@api-new/authservice/HTTP_AP_SignIn_WithProvider_Initialize_Request';
import { HTTP_AP_SignIn_WithProvider_Initialize_Response } from '@api-new/authservice/HTTP_AP_SignIn_WithProvider_Initialize_Response';
import { HTTP_AP_ValidateAdvisorUserResetPasswordCode_Request } from '@api-new/authservice/HTTP_AP_ValidateAdvisorUserResetPasswordCode_Request';
import { HTTP_CP_DeleteClientUserAccessToken_Request } from '@api-new/authservice/HTTP_CP_DeleteClientUserAccessToken_Request';
import { HTTP_CP_DeleteClientUser_WithCode_Request } from '@api-new/authservice/HTTP_CP_DeleteClientUser_WithCode_Request';
import { HTTP_CP_ListClientUserAccessTokens_Response } from '@api-new/authservice/HTTP_CP_ListClientUserAccessTokens_Response';
import { HTTP_CP_ListSignInCompanies_Request } from '@api-new/authservice/HTTP_CP_ListSignInCompanies_Request';
import { HTTP_CP_ListSignInCompanies_Response } from '@api-new/authservice/HTTP_CP_ListSignInCompanies_Response';
import { HTTP_CP_ProvideClientUserSignInCode_Request } from '@api-new/authservice/HTTP_CP_ProvideClientUserSignInCode_Request';
import { HTTP_CP_SignIn_WithCode_Request } from '@api-new/authservice/HTTP_CP_SignIn_WithCode_Request';
import { HTTP_CP_SignIn_WithCode_Response } from '@api-new/authservice/HTTP_CP_SignIn_WithCode_Response';
import { HTTP_CP_SignUp_Request } from '@api-new/authservice/HTTP_CP_SignUp_Request';
import { HTTP_CP_Unsubscribe_WithCode_Request } from '@api-new/authservice/HTTP_CP_Unsubscribe_WithCode_Request';
import { HTTP_CP_Unsubscribe_WithCode_Response } from '@api-new/authservice/HTTP_CP_Unsubscribe_WithCode_Response';
import { Observable } from 'rxjs';
import { ApplicationHttpClient, IRequestOptions } from '../../../../../lib/services/application-http.service';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly httpClient = inject(ApplicationHttpClient);

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- GRPC Endpoints ----------------------------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  // --- HTTP Endpoints - Adviser Portal (ap) ------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  HTTP_AP_DeleteAdvisorUserAccessToken(
    request: HTTP_AP_DeleteAdvisorUserAccessToken_Request,
    options?: IRequestOptions,
  ): Observable<undefined> {
    return this.httpClient.delete(`/auth-service/ap/DeleteAdvisorUserAccessToken`, { params: request });
  }

  HTTP_AP_ImpersonateClientUser(
    request: HTTP_AP_ImpersonateClientUser_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_AP_ImpersonateClientUser_Response> {
    return this.httpClient.post(`/auth-service/ap/ImpersonateClientUser`, request, options);
  }

  HTTP_AP_ListAdvisorUserAccessTokens(
    request?: undefined,
    options?: IRequestOptions,
  ): Observable<HTTP_AP_ListAdvisorUserAccessTokens_Response> {
    return this.httpClient.get(`/auth-service/ap/ListAdvisorUserAccessTokens`, { params: request });
  }

  HTTP_AP_ProvideAdvisorUserResetPasswordCode(
    request: HTTP_AP_ProvideAdvisorUserResetPasswordCode_Request,
    options?: IRequestOptions,
  ): Observable<undefined> {
    return this.httpClient.post(`/auth-service/ap/ProvideAdvisorUserResetPasswordCode`, request, options);
  }

  HTTP_AP_ResetPassword_WithCode(request: HTTP_AP_ResetPassword_WithCode_Request, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.post(`/auth-service/ap/ResetPassword/WithCode`, request, options);
  }

  HTTP_AP_SignIn_WithPassword(
    request: HTTP_AP_SignIn_WithPassword_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_AP_SignIn_WithPassword_Response> {
    return this.httpClient.post(`/auth-service/ap/SignIn/WithPassword`, request, options);
  }

  HTTP_AP_SignIn_WithProvider_Authorize(
    request: HTTP_AP_SignIn_WithProvider_Authorize_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_AP_SignIn_WithProvider_Authorize_Response> {
    return this.httpClient.post(`/auth-service/ap/SignIn/WithProvider/Authorize`, request, options);
  }

  HTTP_AP_SignIn_WithProvider_Initialize(
    request: HTTP_AP_SignIn_WithProvider_Initialize_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_AP_SignIn_WithProvider_Initialize_Response> {
    return this.httpClient.post(`/auth-service/ap/SignIn/WithProvider/Initialize`, request, options);
  }

  HTTP_AP_ValidateAdvisorUserResetPasswordCode(
    request: HTTP_AP_ValidateAdvisorUserResetPasswordCode_Request,
    options?: IRequestOptions,
  ): Observable<undefined> {
    return this.httpClient.post(`/auth-service/ap/ValidateAdvisorUserResetPasswordCode`, request, options);
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- HTTP Endpoints - Client Portal (cp) -------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  HTTP_CP_DeleteClientUser_WithCode(request: HTTP_CP_DeleteClientUser_WithCode_Request, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.post(`/auth-service/cp/DeleteClientUser/WithCode`, request, options);
  }

  HTTP_CP_DeleteClientUserAccessToken(
    request: HTTP_CP_DeleteClientUserAccessToken_Request,
    options?: IRequestOptions,
  ): Observable<undefined> {
    return this.httpClient.delete(`/auth-service/cp/DeleteClientUserAccessToken`, { params: request });
  }

  HTTP_CP_ListClientUserAccessTokens(
    request?: undefined,
    options?: IRequestOptions,
  ): Observable<HTTP_CP_ListClientUserAccessTokens_Response> {
    return this.httpClient.get(`/auth-service/cp/ListClientUserAccessTokens`, { params: request });
  }

  HTTP_CP_ListSignInCompanies(
    request: HTTP_CP_ListSignInCompanies_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_CP_ListSignInCompanies_Response> {
    return this.httpClient.post(`/auth-service/cp/ListSignInCompanies`, request, options);
  }

  HTTP_CP_ProvideClientUserDeleteCode(request?: undefined, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.post(`/auth-service/cp/ProvideClientUserDeleteCode`, request, options);
  }

  HTTP_CP_ProvideClientUserSignInCode(
    request: HTTP_CP_ProvideClientUserSignInCode_Request,
    options?: IRequestOptions,
  ): Observable<undefined> {
    return this.httpClient.post(`/auth-service/cp/ProvideClientUserSignInCode`, request, options);
  }

  HTTP_CP_SignIn_WithCode(
    request: HTTP_CP_SignIn_WithCode_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_CP_SignIn_WithCode_Response> {
    return this.httpClient.post(`/auth-service/cp/SignIn/WithCode`, request, options);
  }

  HTTP_CP_SignOut(request?: undefined, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.post(`/auth-service/cp/SignOut`, request, options);
  }

  HTTP_CP_SignUp(request: HTTP_CP_SignUp_Request, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.post(`/auth-service/cp/SignUp`, request, options);
  }

  HTTP_CP_Unsubscribe_WithCode(
    request: HTTP_CP_Unsubscribe_WithCode_Request,
    options?: IRequestOptions,
  ): Observable<HTTP_CP_Unsubscribe_WithCode_Response> {
    return this.httpClient.post(`/auth-service/cp/Unsubscribe/WithCode`, request, options);
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- HTTP Endpoints - Extended (x) -------------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  // Empty

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- INIT Endpoints ----------------------------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- PUBLIC Endpoints --------------------------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  PUBLIC_UptimeCheck(request?: undefined, options?: IRequestOptions): Observable<undefined> {
    return this.httpClient.get(`/auth-service/.public/UptimeCheck`, { params: request });
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //
  // --- SPECIAL Endpoints -------------------------------------------------------------------------------------------------------------------------------- //
  // ------------------------------------------------------------------------------------------------------------------------------------------------------ //

  // Empty
}
